/**
 * Iter 227 — Timesheet Header Widget.
 * Iter 228 — Multi-entry per task, wider popover + comment, deep link,
 *            weekly rollup chip ("Xh / 40h") from Wednesday onwards.
 *
 * Shows today's total logged hours as a chip in the app header. From Wed
 * onwards the chip switches to "Xh / Yh" against the current user's weekly
 * target (per-user override → workspace default → 40).
 *
 * Click opens a wide popover with each open task on its own row. Users can:
 *   • Fill start/end/comment inline for the primary block (existing flow)
 *   • Click "+" to add a SECOND (or Nth) time block for the same task
 *     without leaving the widget — useful for context-switchers who
 *     touched the same ticket twice in a day.
 *   • Jump to the full My Timesheet page via the footer link.
 */
import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Clock, X, Save, Plus, Loader2, ArrowUpRight, Trash2, LayoutTemplate, Check, Pencil, ListChecks, Copy, Search, Maximize2, Minimize2, ArrowDownAZ, ArrowUpAZ } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { toast } from "sonner";
import { localWallClockIso } from "@/lib/timeSubmit";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import MultiSelect from "@/components/ui/multi-select";
import { apiErrorMessage } from "@/lib/apiError";

const todayISO = () => new Date().toISOString().slice(0, 10);
const hmToMin = (hm) => { if (!hm) return null; const [h, m] = hm.split(":"); return Number(h) * 60 + Number(m); };
const minutesToHours = (m) => Math.round((m / 60) * 100) / 100;

// R52.2 · Age / due badge — most-informative single pill next to the
// task title. Priorities (top → bottom):
//   • overdue → red, "N d overdue"
//   • due today → amber, "Due today"
//   • due soon (< 3d) → amber, "Due in Nd"
//   • has due date > 3d out → slate, "Due Nd"
//   • no due date → shows created-age when > 14d ("14d+ old")
// Feb 2026 · Format a loose date string (date-only `YYYY-MM-DD` or full
// ISO with timezone) into a human-readable `DD Mon YYYY`. Returns empty
// string if the input doesn't parse — callers can conditionally render.
function _fmtDueDate(v) {
  if (!v) return "";
  const d = new Date(String(v).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  const mon = d.toLocaleString("en-US", { month: "short" });
  return `${String(d.getDate()).padStart(2, "0")} ${mon} ${d.getFullYear()}`;
}

function TaskAgeDueBadge({ task }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = task?.due_date ? new Date(String(task.due_date).slice(0, 10) + "T00:00:00") : null;
  const created = task?.created_at ? new Date(String(task.created_at).slice(0, 10) + "T00:00:00") : null;
  const dayDiff = (a, b) => Math.round((a - b) / (1000 * 60 * 60 * 24));

  if (due && !isNaN(due.getTime())) {
    const d = dayDiff(due, today);
    if (isNaN(d)) return null;
    if (d < 0) {
      return <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 bg-rose-600 text-white" data-testid="ts-badge-overdue">{-d}d overdue</span>;
    }
    if (d === 0) {
      return <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 bg-amber-500 text-black" data-testid="ts-badge-today">Due today</span>;
    }
    if (d <= 2) {
      return <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 bg-amber-100 text-amber-900 border border-amber-300" data-testid="ts-badge-soon">Due in {d}d</span>;
    }
    return <span className="text-[9px] font-medium uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 text-slate-700 border border-slate-200">Due {d}d</span>;
  }
  if (created && !isNaN(created.getTime())) {
    const age = dayDiff(today, created);
    if (isNaN(age)) return null;
    if (age >= 14) {
      return <span className="text-[9px] font-semibold uppercase tracking-widest px-1.5 py-0.5 bg-slate-200 text-slate-700 border border-slate-300" data-testid="ts-badge-stale">{age}d old</span>;
    }
    if (age >= 7) {
      return <span className="text-[9px] font-medium uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 text-slate-600">{age}d old</span>;
    }
  }
  return null;
}

export default function TimesheetWidget() {
  const { activeWorkspaceId, user } = useAuth();
  // Feb 2026 · `content_calendar.view` gates the deliverable + topic
  // lines on the task hover-card inside the Timesheet popup, matching
  // the Tasks list + Edit Task popup behaviour.
  const { hasPerm } = useWorkspacePerms();
  const [open, setOpen] = useState(false);
  // R127 · Feb 2026 — Global "open-timesheet-widget" event so the
  // mobile overflow menu can trigger the same popup as the header
  // icon without duplicating the widget shell.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-timesheet-widget", onOpen);
    return () => window.removeEventListener("open-timesheet-widget", onOpen);
  }, []);
  const [today] = useState(todayISO());
  // R138bb · Feb 2026 — Week View. Users can toggle between days in
  // the current ISO week (Mon–Sun) so switching from "today" to
  // "tomorrow" is a single tap. `selectedDate` drives the default
  // date for new rows; the per-row picker still allows override.
  const [selectedDate, setSelectedDate] = useState(todayISO());
  // R138bc · Feb 2026 — Week Strip Multi-week. Users can page forward
  // (unlimited, to plan ahead) or backward (only if their role allows
  // back-dating). `weekOffset=0` is current week; +1 = next week etc.
  const [weekOffset, setWeekOffset] = useState(0);
  const weekDays = useMemo(() => {
    const t = new Date();
    const dow = (t.getDay() + 6) % 7;  // Mon=0..Sun=6
    const mon = new Date(t.getFullYear(), t.getMonth(), t.getDate() - dow + weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + i);
      const iso = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
      return { iso, dayShort: d.toLocaleDateString("en-US", { weekday: "short" }), dayNum: d.getDate() };
    });
  }, [today, weekOffset]);
  const weekStripLabel = useMemo(() => {
    if (weekOffset === 0) return "This week";
    if (weekOffset === 1) return "Next week";
    if (weekOffset === -1) return "Last week";
    if (weekOffset > 0) return `+${weekOffset} weeks`;
    return `${weekOffset} weeks`;
  }, [weekOffset]);
  // Manager back-date permission — a simple opt-in via localStorage
  // ("manager mode"). Backend still enforces `min=today` on submission.
  const [allowBackfill, setAllowBackfill] = useState(() => {
    try { return localStorage.getItem("ts-allow-backfill") === "1"; } catch { return false; }
  });
  // Feb 2026 · Non-managers can't back-fill; strip any stale localStorage
  // flag so a demoted user doesn't still see past-week dates.
  useEffect(() => {
    if (allowBackfill && !hasPerm("workspace.manage")) {
      setAllowBackfill(false);
      try { localStorage.removeItem("ts-allow-backfill"); } catch { /* ignore */ }
    }
  }, [allowBackfill, hasPerm]);
  const [entries, setEntries] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  // R93 · Local search over the open-tasks list. Filters by title or project
  // name; case-insensitive substring match. Cleared on popup close.
  const [taskQuery, setTaskQuery] = useState("");
  // Feb 2026 · Additional filters requested by users to slice down large
  // open-task lists inside the widget:
  //   • Project multi-select (sorted, searchable)  ← MultiSelect chip
  //   • Due-range quick chips: past-due / today / pending / upcoming / all
  //   • Sort by created_at / due_date / updated_at (asc | desc)
  //   • Full-screen mode (99vw × 96vh)
  const [projectFilter, setProjectFilter] = useState([]);      // array of project_ids
  const [dueRange,      setDueRange]      = useState("all");    // past|today|pending|upcoming|all
  const [sortBy,        setSortBy]        = useState("due_date"); // due_date|created_at|updated_at
  const [sortDir,       setSortDir]       = useState("asc");    // asc|desc
  const [fullscreen,    setFullscreen]    = useState(false);
  // Feb 2026 · Persist filter/sort preferences per user so the widget
  // opens exactly as they left it. Scoped by workspace + user id so
  // switching workspaces doesn't leak state across contexts.
  const prefsKey = useMemo(() => {
    if (!activeWorkspaceId || !user?.id) return null;
    return `ts-widget-prefs:${activeWorkspaceId}:${user.id}`;
  }, [activeWorkspaceId, user?.id]);
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!prefsKey || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(prefsKey);
      if (raw) {
        const p = JSON.parse(raw);
        if (Array.isArray(p.projectFilter)) setProjectFilter(p.projectFilter);
        if (typeof p.dueRange === "string") setDueRange(p.dueRange);
        if (typeof p.sortBy === "string")   setSortBy(p.sortBy);
        if (typeof p.sortDir === "string")  setSortDir(p.sortDir);
      }
    } catch { /* corrupt entry — ignore */ }
    hydratedRef.current = true;
  }, [prefsKey]);
  useEffect(() => {
    if (!prefsKey || !hydratedRef.current) return;
    try {
      localStorage.setItem(prefsKey, JSON.stringify({
        projectFilter, dueRange, sortBy, sortDir,
      }));
    } catch { /* quota — ignore */ }
  }, [prefsKey, projectFilter, dueRange, sortBy, sortDir]);
  const projectOptions = useMemo(() => {
    const seen = new Map();
    for (const t of tasks) {
      if (!t.project_id || seen.has(t.project_id)) continue;
      seen.set(t.project_id, { value: t.project_id, label: t.project_name || "Untitled project" });
    }
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks]);
  // Feb 2026 · Overdue nudge — count open tasks whose due_date is
  // before today so managers spot slippage the moment the popup opens.
  // Dismissible for the current widget-open session; reappears next
  // time the widget is re-opened if anything is still overdue.
  const overdueCount = useMemo(() => {
    const t = todayISO();
    return (tasks || []).filter((x) => x.due_date && String(x.due_date) < t).length;
  }, [tasks]);
  const [overdueDismissed, setOverdueDismissed] = useState(false);
  useEffect(() => { if (!open) setOverdueDismissed(false); }, [open]);
  const filteredTasks = useMemo(() => {
    const q = (taskQuery || "").trim().toLowerCase();
    const todayISOStr = todayISO();
    const inDueRange = (t) => {
      if (dueRange === "all") return true;
      const d = t.due_date || "";
      if (dueRange === "past")    return d && d < todayISOStr;
      if (dueRange === "today")   return d === todayISOStr;
      if (dueRange === "pending") return d && d <= todayISOStr;
      if (dueRange === "upcoming") return d && d > todayISOStr;
      return true;
    };
    let list = tasks.filter((t) => {
      if (projectFilter.length && !projectFilter.includes(t.project_id)) return false;
      if (!inDueRange(t)) return false;
      if (q) {
        const hay = `${t.title || ""} ${t.project_name || ""} ${t.brand_name || ""} ${t.client_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // Sort — nulls/empty strings pushed to the end regardless of dir so
    // the important dated tasks always surface first.
    list = [...list].sort((a, b) => {
      const av = a?.[sortBy] || "";
      const bv = b?.[sortBy] || "";
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortDir === "asc" ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
    return list;
  }, [tasks, taskQuery, projectFilter, dueRange, sortBy, sortDir]);
  const [weekProgress, setWeekProgress] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [tplMenuOpen, setTplMenuOpen] = useState(false);
  const [tplSaving, setTplSaving] = useState(false);
  const [tplApplying, setTplApplying] = useState(null);
  const rootRef = useRef(null);

  const loadEntries = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/time-entries?date=${today}`);
      setEntries(Array.isArray(data) ? data : []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  };

  const loadTasks = async () => {
    if (!activeWorkspaceId || !user?.id) return;
    try {
      // Feb 2026 R292 · Perf+correctness — the earlier 100-task cap was
      // cutting off users who had legitimate assignments beyond 100
      // (reported by Pratik with 189 tasks). The backend fetch is cheap
      // thanks to `lean=true` and the composite index on
      // (workspace_id, assignee_id, status), so we raise the cap to the
      // backend's hard ceiling (5000) and lean the projection so title /
      // project / due / status / assignee come down fast.
      //
      // Feb 2026 R293 · Server-side `due_scope` chip so a user with
      // 1,000+ open assignments only downloads the slice their filter
      // actually needs (past/today/pending/upcoming). The server knows
      // the "today" reference date and clamps `due_date` on the Mongo
      // query so the response is small and already sorted by relevance.
      const scopeParam =
        dueRange && dueRange !== "all" ? `&due_scope=${dueRange}` : "";
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/tasks?assignee_id=${user.id}&status=open,in_progress,blocked&limit=5000&lean=true${scopeParam}`,
      );
      // Oct 2026 (round 2) · Defense-in-depth narrow — user spec:
      // "A user can only submit timesheet on tasks they are part of."
      // So we re-apply the narrow filter client-side (assignee /
      // collaborator / subtask assignee), guarding against any legacy
      // backend build or scope expansion that might leak project-owner
      // tasks into this picker.
      const uid = user.id;
      const isMine = (t) => {
        if (t.assignee_id === uid) return true;
        const collabs = Array.isArray(t.collaborators) ? t.collaborators : [];
        if (collabs.some((c) => (c && (c.user_id === uid || c === uid)))) return true;
        const subs = Array.isArray(t.subtasks) ? t.subtasks : [];
        return subs.some((s) => s && s.assignee_id === uid);
      };
      const active = (Array.isArray(data) ? data : []).filter(
        (t) => isMine(t) && t.status !== "done" && !t.deleted_at,
      );
      setTasks(active);
    } catch { setTasks([]); }
  };

  const loadWeekProgress = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/timesheet/week-progress?date=${today}`);
      setWeekProgress(data);
    } catch { setWeekProgress(null); }
  };

  const loadTemplates = async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/timesheet/templates`);
      setTemplates(Array.isArray(data) ? data : []);
    } catch { setTemplates([]); }
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name (e.g. 'Standard day')");
    if (!name || !name.trim()) return;
    setTplSaving(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/timesheet/templates`,
                          { name: name.trim(), source_date: today });
      toast.success(`Saved "${name.trim()}"`);
      loadTemplates();
      setTplMenuOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setTplSaving(false); }
  };

  const applyTemplate = async (tpl) => {
    setTplApplying(tpl.id);
    try {
      const { data } = await api.post(`/timesheet-templates/${tpl.id}/apply?date=${today}`);
      toast.success(`Applied "${tpl.name}" · ${data.copied} entries copied${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}`);
      loadEntries(); loadWeekProgress();
      setTplMenuOpen(false);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Apply failed"));
    } finally { setTplApplying(null); }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/timesheet-templates/${tpl.id}`);
      toast.success("Deleted");
      loadTemplates();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    }
  };

  // R64 · Bulk-duplicate the most recent non-empty day's time entries
  // into `today`. Uses the existing POST /timesheet/copy-from-previous
  // endpoint (auto-walks back up to 7 days). Skipped rows (overlap
  // conflict) are surfaced in the success toast so the user knows to
  // fix them by hand.
  const [dupBusy, setDupBusy] = useState(false);
  const duplicateYesterday = async () => {
    setDupBusy(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/timesheet/copy-from-previous?date=${today}`,
      );
      const skipped = (data.skipped || []).length;
      toast.success(
        `Copied ${data.copied} entr${data.copied === 1 ? "y" : "ies"} from ${data.source_date}` +
        (skipped ? ` · ${skipped} skipped (overlap)` : ""),
      );
      loadEntries(); loadWeekProgress();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Nothing to duplicate — no entries in the past 7 days"));
    } finally { setDupBusy(false); }
  };

  useEffect(() => { loadEntries(); loadWeekProgress();  }, [activeWorkspaceId, today]);
  // Feb 2026 R291q · Cache the open-popup payload for 60 s so flipping
  // the pill back open within a minute is instant. `tasksCacheRef`
  // stores a last-fetch timestamp; the templates list is pulled once
  // per workspace because templates rarely change mid-session.
  // Oct 2026 · Cache key now includes `user.id` so impersonation (which
  // keeps the workspace stable but swaps the viewer) ALWAYS refetches
  // tasks instead of silently showing the previous viewer's list.
  const tasksCacheRef = useRef({ at: 0, ws: null, uid: null, scope: null });
  const templatesLoadedForWsRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const now = Date.now();
    const cache = tasksCacheRef.current;
    if (cache.ws !== activeWorkspaceId
        || cache.uid !== user?.id
        || cache.scope !== dueRange
        || (now - cache.at) > 60_000) {
      loadTasks();
      tasksCacheRef.current = { at: now, ws: activeWorkspaceId, uid: user?.id, scope: dueRange };
    }
    if (templatesLoadedForWsRef.current !== activeWorkspaceId) {
      loadTemplates();
      templatesLoadedForWsRef.current = activeWorkspaceId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeWorkspaceId, user?.id, dueRange]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const totalHours = useMemo(() => {
    const total = entries.reduce((s, e) => s + (Number(e.hours) || 0), 0);
    return Math.round(total * 100) / 100;
  }, [entries]);

  // Group existing entries by task_id → sorted by created_at so the oldest
  // entry (typically the one the user filled first) is the "primary" row.
  const entryByTask = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => { if (!map.has(e.task_id)) map.set(e.task_id, []); map.get(e.task_id).push(e); });
    for (const arr of map.values()) arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return map;
  }, [entries]);

  const totalStyle = totalHours >= 8 ? "bg-emerald-500 text-white" : totalHours > 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground";
  const showWeek = !!(weekProgress && weekProgress.show_target);
  // Oct 2026 · Round week-hours for display so floating-point noise
  // like `5.229999999999...h` never shows up in the header pill.
  const weekHoursDisplay = weekProgress
    ? Math.round((Number(weekProgress.hours_this_week) || 0) * 100) / 100
    : 0;
  const weekLabel = showWeek
    ? `${weekHoursDisplay}h / ${Math.round(weekProgress.weekly_target_hours)}h`
    : `${totalHours}h`;
  const weekTitle = showWeek
    ? `Week: ${weekHoursDisplay}h logged of ${weekProgress.weekly_target_hours}h target · Today: ${totalHours}h`
    : `Today's logged hours · ${today}`;

  return (
    <div className="relative" ref={rootRef} data-testid="timesheet-widget">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs font-semibold px-1.5 sm:px-2.5 py-1 sm:py-1.5 sm:border sm:border-border ${totalStyle} hover:opacity-90 transition`}
        data-testid="timesheet-widget-chip"
        title={weekTitle}
      >
        <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
        <span className="tabular-nums" data-testid="timesheet-widget-chip-label">{weekLabel}</span>
      </button>
      {open && (
        // R64.7 · Fixed-position full-width panel — anchoring to the
        // trigger via `absolute right-0` was pushing the panel past the
        // LEFT viewport edge on smaller screens. Fixed placement lets
        // us span the whole viewport width safely.
        // R64.9 · Thick 2px primary border + deep shadow so the popup
        // reads as a distinct floating surface, not a page section.
        <div className={fullscreen
              ? "fixed inset-x-2 top-2 h-[calc(100vh-1rem)] max-w-none bg-background border-2 border-primary/70 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] z-[90] flex flex-col overflow-hidden"
              : "fixed left-2 right-2 top-14 h-[calc(100vh-4rem)] sm:h-auto sm:max-h-[calc(100vh-4rem)] max-w-none bg-background border-2 border-primary/70 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] z-[90] flex flex-col overflow-hidden"}
             data-clear-impersonation="true" data-testid="timesheet-widget-popover">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between bg-muted/40 relative gap-3">
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Today · {today}</span>
              <span className="text-sm font-bold tabular-nums" data-testid="timesheet-widget-today-total">{totalHours}h</span>
              <span className="text-[10px] text-muted-foreground">logged</span>
            </div>
            {weekProgress && (
              <div className="flex items-baseline gap-2" data-testid="timesheet-widget-week-summary">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">This week</span>
                <span className="text-sm font-bold tabular-nums">
                  {Math.round((Number(weekProgress.hours_this_week) || 0) * 100) / 100}h
                  <span className="text-muted-foreground text-[11px] font-medium"> / {Math.round(weekProgress.weekly_target_hours)}h</span>
                </span>
              </div>
            )}
            <div className="flex items-center gap-1 ml-auto">
              <button
                type="button"
                onClick={duplicateYesterday}
                disabled={dupBusy}
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-1 border border-border hover:bg-muted transition disabled:opacity-50"
                data-testid="timesheet-widget-duplicate-yday-btn"
                title="Duplicate yesterday's plan into today"
              >
                {dupBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                <span className="hidden md:inline">Duplicate yesterday</span>
                <span className="md:hidden">Dup y&apos;day</span>
              </button>
              <button
                type="button"
                onClick={() => setTplMenuOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-1 border border-border hover:bg-muted transition"
                data-testid="timesheet-widget-templates-btn"
                title="Timesheet templates"
              >
                <LayoutTemplate className="w-3 h-3" />
                <span className="hidden sm:inline">Templates</span>
              </button>
              <button
                type="button"
                onClick={() => setFullscreen((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] px-1.5 py-1 border border-border hover:bg-muted transition"
                data-testid="timesheet-widget-fullscreen-btn"
                title={fullscreen ? "Exit full screen" : "Full screen"}
              >
                {fullscreen ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                <span className="hidden sm:inline">{fullscreen ? "Exit" : "Full"}</span>
              </button>
              <button type="button" onClick={() => setOpen(false)} className="p-0.5 text-muted-foreground hover:text-foreground" data-testid="timesheet-widget-close">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            {tplMenuOpen && (
              <div className="absolute right-4 top-full mt-1 w-72 bg-background border border-border shadow-xl z-[91]" data-testid="timesheet-widget-templates-menu">
                <button
                  type="button"
                  onClick={saveTemplate}
                  disabled={tplSaving}
                  className="w-full text-left px-3 py-2 text-xs font-semibold hover:bg-muted border-b border-border flex items-center gap-2 disabled:opacity-50"
                  data-testid="timesheet-widget-tpl-save"
                >
                  {tplSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Save today as a new template…
                </button>
                <div className="max-h-64 overflow-y-auto">
                  {templates.length === 0 ? (
                    <div className="px-3 py-4 text-[11px] text-muted-foreground text-center">No templates yet. Save today first.</div>
                  ) : templates.map((tpl) => (
                    <div key={tpl.id} className="px-3 py-2 flex items-center gap-2 hover:bg-muted/60 border-b border-border last:border-b-0" data-testid={`timesheet-widget-tpl-row-${tpl.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{tpl.name}</div>
                        <div className="text-[10px] text-muted-foreground">{(tpl.entries || []).length} entries</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => applyTemplate(tpl)}
                        disabled={tplApplying === tpl.id}
                        className="text-[10px] px-2 py-0.5 bg-primary text-primary-foreground hover:opacity-90 inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`timesheet-widget-tpl-apply-${tpl.id}`}
                      >
                        {tplApplying === tpl.id ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5" />}
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteTemplate(tpl)}
                        className="text-muted-foreground hover:text-rose-600"
                        data-testid={`timesheet-widget-tpl-del-${tpl.id}`}
                        title="Delete template"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={fullscreen ? "flex-1 min-h-0 overflow-y-auto" : "flex-1 min-h-0 overflow-y-auto sm:flex-none sm:max-h-[65vh]"}>
            {overdueCount > 0 && !overdueDismissed && (
              <div
                className="mx-3 mt-3 mb-1 border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2 flex items-center gap-2 text-xs"
                data-testid="ts-widget-overdue-banner"
              >
                <span className="inline-flex items-center justify-center w-5 h-5 bg-rose-600 text-white text-[10px] font-bold rounded-full shrink-0" data-testid="ts-widget-overdue-count">
                  {overdueCount}
                </span>
                <span className="flex-1">
                  <b>{overdueCount}</b> open task{overdueCount === 1 ? "" : "s"} {overdueCount === 1 ? "is" : "are"} past their due date. Log time on them soon or tap
                  <button
                    type="button"
                    onClick={() => setDueRange("past")}
                    className="underline underline-offset-2 font-semibold text-rose-900 mx-1 hover:text-rose-950"
                    data-testid="ts-widget-overdue-focus"
                  >
                    show only past due
                  </button>
                  to focus on them.
                </span>
                <button
                  type="button"
                  onClick={() => setOverdueDismissed(true)}
                  className="text-rose-700 hover:text-rose-900 shrink-0"
                  title="Dismiss"
                  data-testid="ts-widget-overdue-dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <TimesheetTaskSearchBar
              query={taskQuery}
              onChange={setTaskQuery}
              matchCount={filteredTasks.length}
              totalCount={tasks.length}
              projectOptions={projectOptions}
              projectFilter={projectFilter}
              onProjectFilterChange={setProjectFilter}
              dueRange={dueRange}
              onDueRangeChange={setDueRange}
              sortBy={sortBy}
              sortDir={sortDir}
              onSortByChange={setSortBy}
              onSortDirChange={setSortDir}
            />
            {loading && tasks.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground">Loading…</div>
            ) : tasks.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground">You don&apos;t have any open tasks. Try creating one first.</div>
            ) : filteredTasks.length === 0 ? (
              <div className="p-8 text-center text-xs text-muted-foreground" data-testid="ts-widget-search-empty">
                No tasks match the current search / filters. Clear them to see all {tasks.length} open tasks.
              </div>
            ) : (
              <>
              {/* R138bb · Week View strip with R138bc multi-week arrows */}
              <div className="flex items-center gap-1 px-3 py-2 border-b border-border bg-muted/30 flex-wrap"
                   data-testid="timesheet-week-strip">
                <button type="button"
                        onClick={() => setWeekOffset((w) => w - 1)}
                        disabled={weekOffset <= -1 && !allowBackfill}
                        title={weekOffset <= -1 && !allowBackfill ? "Back-fill needs manager mode" : "Previous week"}
                        className="text-[10px] px-1.5 py-1 border border-border hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="timesheet-week-prev">
                  ←
                </button>
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground mx-1 min-w-[70px] text-center">
                  {weekStripLabel}
                </span>
                <button type="button"
                        onClick={() => setWeekOffset((w) => w + 1)}
                        disabled={weekOffset >= 4}
                        title={weekOffset >= 4 ? "Cannot plan more than 4 weeks ahead" : "Next week"}
                        className="text-[10px] px-1.5 py-1 border border-border hover:bg-background disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid="timesheet-week-next">
                  →
                </button>
                {weekOffset !== 0 && (
                  <button type="button" onClick={() => setWeekOffset(0)}
                          className="text-[9px] uppercase tracking-widest px-1.5 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                          data-testid="timesheet-week-today-btn">
                    Today
                  </button>
                )}
                {weekDays.map((d) => {
                  const isToday = d.iso === today;
                  const isSel = d.iso === selectedDate;
                  const isPast = d.iso < today && !allowBackfill;
                  return (
                    <button key={d.iso} type="button"
                            onClick={() => !isPast && setSelectedDate(d.iso)}
                            disabled={isPast}
                            title={isPast ? "Enable manager mode to back-date" : (isToday ? "Today" : `Set default to ${d.iso}`)}
                            className={`px-2 py-1 text-[10px] flex flex-col items-center border ${
                              isSel ? "bg-primary text-primary-foreground border-primary font-bold"
                              : isPast ? "border-border/40 text-muted-foreground/50 cursor-not-allowed line-through"
                              : isToday ? "border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                              : "border-border hover:bg-background"}`}
                            data-testid={`timesheet-week-day-${d.iso}`}>
                      <span className="uppercase tracking-widest text-[8px] leading-none">{d.dayShort}</span>
                      <span className="text-[13px] font-mono tabular-nums leading-tight">{d.dayNum}</span>
                    </button>
                  );
                })}
                <label className={`ml-auto flex items-center gap-1 text-[9px] uppercase tracking-widest text-muted-foreground cursor-pointer ${hasPerm("workspace.manage") ? "" : "hidden"}`}
                        title="Manager mode: allow selecting past dates. Backend still enforces write permissions.">
                  <input type="checkbox" checked={allowBackfill}
                          onChange={(e) => {
                            const v = e.target.checked;
                            setAllowBackfill(v);
                            try { localStorage.setItem("ts-allow-backfill", v ? "1" : "0"); } catch { /* ignore */ }
                          }}
                          className="w-3 h-3"
                          data-testid="timesheet-allow-backfill" />
                  Back-fill
                </label>
              </div>
              <table className="w-full text-[11px] table-fixed">
                <colgroup>
                  <col style={{ width: 40 }} />
                  <col style={{ width: 240 }} />
                  <col style={{ width: 148 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: 92 }} />
                  <col />
                  <col style={{ width: 220 }} />
                </colgroup>
                <thead className="sticky top-0 bg-muted/60 backdrop-blur border-b border-border">
                  <tr className="text-left text-[9px] uppercase text-muted-foreground">
                    <th className="px-2 py-2 text-right font-semibold" data-testid="timesheet-col-rownum">#</th>
                    <th className="px-3 py-2">Task</th>
                    <th className="px-2 py-2">Date <span className="text-rose-600" aria-label="required">*</span></th>
                    <th className="px-2 py-2">Start <span className="text-rose-600" aria-label="required">*</span></th>
                    <th className="px-2 py-2">End <span className="text-rose-600" aria-label="required">*</span></th>
                    <th className="px-2 py-2 text-right">Hours <span className="text-rose-600" aria-label="required">*</span></th>
                    <th className="px-2 py-2">Comment <span className="text-rose-600" aria-label="required">*</span></th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map((t, tIdx) => (
                    <TaskBlock
                      key={t.id}
                      task={t}
                      rowIndex={tIdx + 1}
                      today={today}
                      defaultDate={selectedDate}
                      wsId={activeWorkspaceId}
                      existing={entryByTask.get(t.id) || []}
                      isFirstTaskBlock={tIdx === 0}
                      onChange={() => { loadEntries(); loadWeekProgress(); }}
                    />
                  ))}
                </tbody>
              </table>
              </>
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-border bg-muted/20 text-[11px] text-muted-foreground flex items-center justify-between gap-3">
            <span>Enter start + end (HH:MM) — hours auto-calculate. Click <Plus className="w-2.5 h-2.5 inline -mt-0.5" /> to log a second block on the same task.</span>
            <div className="flex items-center gap-3">
              <Link
                to="/app/tasks"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 font-semibold hover:underline"
                data-testid="timesheet-widget-tasks-link"
                title="Open the full Tasks module"
              >
                <ListChecks className="w-3 h-3" /> Tasks module
              </Link>
              <Link
                to="/app/timesheet"
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
                data-testid="timesheet-widget-deep-link"
              >
                Open My Timesheet <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TaskBlock({ task, rowIndex, today, defaultDate, wsId, existing, isFirstTaskBlock, onChange }) {
  // Track how many extra blank rows the user has added on top of `existing`.
  // Each blank row gets a unique key so React doesn't recycle inputs.
  const [extras, setExtras] = useState([]);   // array of strings (row keys)

  // R138bm · Lift the optimistic status change up to the TaskBlock so
  // ALL rows in this task (existing + extras) unlock/lock the instant
  // the user flips the status via any row's badge. Previously each
  // TimesheetRow held its own local `pendingStatus`, so status
  // changes from the first row didn't propagate to blocks added
  // below via the "+" button.
  const [pendingStatus, setPendingStatus] = useState(null);
  const displayStatus = (pendingStatus || task.status || "open").toLowerCase();

  const addExtra = () => setExtras((xs) => [...xs, `new-${Date.now()}-${xs.length}`]);

  // Rows: one per existing entry + one always-empty "primary" if no existing,
  // + every extra the user clicked "+" for.
  const rows = existing.length > 0
    ? [...existing.map((e) => ({ key: e.id, entry: e })), ...extras.map((k) => ({ key: k, entry: null }))]
    : [{ key: "primary", entry: null }, ...extras.map((k) => ({ key: k, entry: null }))];

  return (
    <>
      {rows.map((r, idx) => (
        <TimesheetRow
          key={r.key}
          task={task}
          rowIndex={rowIndex}
          today={today}
          defaultDate={defaultDate}
          wsId={wsId}
          existing={r.entry}
          isFirstRow={idx === 0}
          canAddMore={idx === rows.length - 1}
          // R64.1 · Subtle-but-thick divider between different tasks
          // (only on the first row of a TaskBlock, and only when NOT the
          // very first TaskBlock in the popup). Multi-block rows within
          // the SAME task keep the default 1px divider so they read as
          // sibling entries of one task, not separate tasks.
          isTaskSeparator={idx === 0 && !isFirstTaskBlock}
          // R138bm · Shared status state — all rows read/write the same
          // TaskBlock-level `pendingStatus`.
          sharedStatus={displayStatus}
          onSharedStatusChange={setPendingStatus}
          onAdd={addExtra}
          onDone={() => { setExtras((xs) => xs.filter((k) => k !== r.key)); onChange(); }}
        />
      ))}
    </>
  );
}

function TimesheetRow({ task, rowIndex, today, defaultDate, wsId, existing, isFirstRow, canAddMore, isTaskSeparator, sharedStatus, onSharedStatusChange, onAdd, onDone }) {
  // Oct 2026 · Need `user` here so we can show the primary assignee's
  // name next to tasks where the viewer isn't the primary assignee
  // (collaborator / subtask-assignee contexts).
  const { user } = useAuth();
  // Feb 2026 · `hasPerm` is used below to gate the Content-Calendar
  // deliverable/topic rows in the HoverCard. Previously only the
  // parent `TimesheetWidget` called `useWorkspacePerms()`, so when
  // the HoverCard rendered inside this child it threw
  // "hasPerm is not defined". Call the hook directly here.
  const { hasPerm } = useWorkspacePerms();
  const [start, setStart] = useState(existing?.start_time || "");
  const [end, setEnd] = useState(existing?.end_time || "");
  const [hours, setHours] = useState(existing?.hours ? String(existing.hours) : "");
  const [note, setNote] = useState(existing?.note || "");
  // R138ba · Feb 2026 — Per-row date picker. Defaults to the widget-
  // level "selected date" (which the Week strip controls). R138bb —
  // reacts to `defaultDate` changes: when the user taps a different
  // day in the week strip, freshly rendered rows pick it up.
  const [entryDate, setEntryDate] = useState(existing?.date || defaultDate || today);
  useEffect(() => {
    if (!existing && defaultDate) setEntryDate(defaultDate);
  }, [defaultDate, existing]);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // R63 · state machine: planned → submitted → approved. `planned` rows are
  // editable (user is still planning their day). `submitted`+`approved`
  // rows are read-only (locked from further edits). Rejected rows are
  // editable again — the flow effectively rolls them back to `planned`.
  const rowState = existing?.state || "submitted";
  const isLocked = existing && (rowState === "submitted" || rowState === "approved");
  // R138bm · Read the shared status lifted to TaskBlock so every row
  // (existing + extras added via "+") reacts to the same status flip.
  // Falls back to the raw task.status if no parent context (defensive).
  const displayStatus = (sharedStatus || task.status || "open").toLowerCase();
  // R138bg · Reactive lock — use `displayStatus` (which reflects the
  // inline pending status change) so time-block inputs unlock the
  // instant the user flips the task to "in progress" and re-lock when
  // they flip it back.
  const isInProgress = displayStatus === "in_progress";
  const readOnly = isLocked || (!isInProgress && !existing);
  const canEditStatus = true; // backend enforces task.edit_others; we let anyone try, showing a toast if rejected

  useEffect(() => {
    // Auto-compute hours when start + end are both set
    const s = hmToMin(start), e = hmToMin(end);
    if (s !== null && e !== null && e > s) {
      const h = minutesToHours(e - s);
      setHours(String(h));
    }
     
  }, [start, end]);

  const canSave = touched && Number(hours) > 0 && note.trim().length > 0;
  // R64.1 · Submit should work for EXISTING planned rows even without
  // re-editing (user just wants to lock what's already saved). For fresh
  // rows we still require touched+hours+comment since there's nothing
  // to submit otherwise. Feb 2026 · Comment is now mandatory for every
  // row (planned or submit) so we always require note.trim().
  const canSubmit = (existing ? Number(hours) > 0 && note.trim().length > 0 : canSave);

  const save = async (state = "planned") => {
    setSaving(true);
    try {
      // R138ba · If the row's date is in the future OR the block end
      // hasn't happened yet, auto-flip state to `planned` so the API
      // doesn't reject the row. The user can submit later.
      let effectiveState = state;
      if (entryDate !== today) {
        effectiveState = "planned";
      } else if (state === "submitted" && end) {
        const now = new Date();
        const blockEnd = new Date(`${entryDate}T${end}:00`);
        if (now.getTime() < blockEnd.getTime()) effectiveState = "planned";
      }
      const clientNow = new Date();
      const clientNowIso = new Date(clientNow.getTime() - clientNow.getTimezoneOffset() * 60000).toISOString().slice(0, 19);
      const payload = {
        date: entryDate, task_id: task.id,
        hours: Number(hours),
        start_time: start || null, end_time: end || null,
        note: note?.trim() || null,
        state: effectiveState,
        client_now: clientNowIso,
      };
      if (existing) {
        await api.patch(`/time-entries/${existing.id}`, {
          hours: payload.hours,
          start_time: payload.start_time,
          end_time: payload.end_time,
          note: payload.note,
        });
      } else {
        if (!payload.start_time || !payload.end_time) {
          delete payload.start_time; delete payload.end_time;
        }
        await api.post(`/workspaces/${wsId}/time-entries`, payload);
      }
      toast.success(state === "submitted" ? "Timesheet submitted" : "Saved as planned");
      setTouched(false);
      onDone();
    } catch (e) {
      // Feb 2026 R112 · The backend rejects time entries against
      // non-`in_progress` tasks with a structured 422. Surface a rich
      // toast with a "Move to In Progress & log" action so users
      // don't have to leave the widget to unblock themselves.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 422
          && detail && typeof detail === "object"
          && detail.code === "task_not_in_progress") {
        toast.error(detail.message || `Task must be In Progress to log time.`, {
          duration: 8000,
          action: {
            label: "Move to In Progress & log",
            onClick: async () => {
              try {
                await api.patch(`/tasks/${task.id}`, { status: "in_progress" });
                toast.success("Task moved to In Progress — retrying save…");
                await save(state);
              } catch (patchErr) {
                toast.error(apiErrorMessage(patchErr, "Couldn't move task — check your task-edit permission."));
              }
            },
          },
        });
      } else {
        toast.error(typeof detail === "string" ? detail : "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const submitEntry = async () => {
    if (!existing) {
      // Save fresh entry directly as submitted
      return save("submitted");
    }
    setSubmitting(true);
    try {
      // If user edited fields before submitting, patch first
      if (touched) {
        await api.patch(`/time-entries/${existing.id}`, {
          hours: Number(hours),
          start_time: start || null,
          end_time: end || null,
          note: note?.trim() || null,
        });
      }
      await api.post(`/time-entries/${existing.id}/submit`,
                              { client_now: localWallClockIso() });
      toast.success("Timesheet submitted for approval");
      setTouched(false);
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async () => {
    if (!existing) { onDone(); return; }
    setDeleting(true);
    try {
      await api.delete(`/time-entries/${existing.id}`);
      toast.success("Entry removed");
      onDone();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  };

  const mark = () => setTouched(true);

  return (
    <>
    <tr data-testid={`ts-row-${task.id}${isFirstRow ? "" : "-extra"}`}
        className={
          /* R63 · Row background reflects the state machine so the
                 swimlane / grid gives instant visual clarity. */
          [
            rowState === "planned" ? "bg-amber-50/60"
            : rowState === "approved" ? "bg-emerald-50/60"
            : rowState === "rejected" ? "bg-rose-50/60"
            : rowState === "submitted" ? "bg-sky-50/50"
            : "",
            /* R64.1 · Thick separator between DIFFERENT tasks (only on
                 first row of every task-block except the very first one).
                 Multi-block rows within the SAME task use a subtle 1px
                 divider so they read as sibling entries. */
            isTaskSeparator ? "border-t-4 border-slate-300"
                             : (!isFirstRow ? "border-t border-border/40" : ""),
          ].filter(Boolean).join(" ")
        }>
      {/* Feb 2026 R292 · Row-number sidebar. Only renders a visible
          number on the first row of each TaskBlock so split-blocks
          within one task stay visually grouped. Narrow, mono so the
          numbers line up even when some tasks spill to 2+ rows. */}
      <td className="px-2 py-2 align-top text-right text-[10px] font-mono text-muted-foreground select-none"
          data-testid={isFirstRow ? `ts-row-num-${task.id}` : undefined}>
        {isFirstRow ? rowIndex : ""}
      </td>
      <td className="px-3 py-2 align-top">
        {isFirstRow ? (
          <>
            <div className="flex items-start gap-1.5 group relative">
              <div className="min-w-0 flex-1">
                <HoverCard openDelay={250}>
                  <HoverCardTrigger asChild>
                    <div className="flex items-center gap-1.5 flex-wrap cursor-help">
                      {/* R52.2 · Wider popover + no truncation on the task
                            title. R62 · Switched to Radix HoverCard so
                            popover flips above the trigger automatically
                            for the last 3 rows (was getting clipped). */}
                      <span className="font-medium leading-tight" title={task.title} data-testid={`ts-task-title-${task.id}`}>{task.title}</span>
                      <TaskAgeDueBadge task={task} />
                      {/* Feb 2026 R117 · Interactive status badge — click
                          to change status. Non-in-progress statuses
                          also gray out the inputs below; flipping to
                          "In Progress" unlocks them instantly. Uses
                          the same task.edit_others perm the PATCH
                          endpoint enforces server-side (403 → toast). */}
                      {(() => {
                        const s = displayStatus;
                        const inProg = s === "in_progress";
                        const label = s.replace(/_/g, " ");
                        const cls = inProg
                          ? "border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                          : s === "blocked"
                            ? "border-rose-500 bg-rose-50 text-rose-800 hover:bg-rose-100"
                            : s === "done"
                              ? "border-slate-400 bg-slate-100 text-slate-600 hover:bg-slate-200"
                              : s === "pending_approval"
                                ? "border-violet-500 bg-violet-50 text-violet-800 hover:bg-violet-100"
                                : s === "review"
                                  ? "border-sky-500 bg-sky-50 text-sky-800 hover:bg-sky-100"
                                  : "border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100";
                        const options = [
                          { v: "pending_approval", l: "Pending approval" },
                          { v: "open",             l: "Open" },
                          { v: "in_progress",      l: "In progress" },
                          { v: "review",           l: "Review" },
                          { v: "blocked",          l: "Blocked" },
                          { v: "done",             l: "Done" },
                        ];
                        return (
                          <select
                            value={s}
                            onChange={async (e) => {
                              const next = e.target.value;
                              const prev = displayStatus;
                              onSharedStatusChange?.(next);
                              try {
                                await api.patch(`/tasks/${task.id}`, { status: next });
                                toast.success(`Task marked ${next.replace(/_/g, " ")}`);
                                onDone?.();
                              } catch (err) {
                                onSharedStatusChange?.(prev || "open");
                                const detail = err?.response?.data?.detail;
                                const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" ? detail : "Couldn't change status");
                                toast.error(msg);
                              }
                            }}
                            disabled={!canEditStatus}
                            className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border cursor-pointer appearance-none ${cls}`}
                            title={inProg
                              ? "Task is in progress. Click to change status."
                              : `Click to move to In Progress so you can log time. Task is "${label}".`}
                            data-testid={`ts-task-status-${task.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {options.map((o) => (
                              <option key={o.v} value={o.v}>{o.l}</option>
                            ))}
                          </select>
                        );
                      })()}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent side="right" align="start" className="w-[420px] p-3 rounded-none" data-testid={`ts-task-popover-${task.id}`}>
                    <div className="text-sm font-semibold mb-1 break-words">{task.title}</div>
                    {existing && (
                      /* R64.9 · Mirror the Daily-Plan block hover — show
                           the entry's state / hours / rating / notes /
                           reviewer comment so users get the same rich
                           info here as on the Daily Plan swimlane. */
                      <div className="border-t border-b border-border/60 my-1.5 py-1.5 space-y-1" data-testid={`ts-entry-info-${existing.id}`}>
                        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                          <span className={`inline-flex items-center uppercase tracking-widest font-semibold px-1.5 py-0.5 border ${
                            existing.state === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-800" :
                            existing.state === "planned"  ? "border-amber-500 bg-amber-50 text-amber-800" :
                            existing.state === "rejected" ? "border-rose-500 bg-rose-50 text-rose-800" :
                            "border-sky-500 bg-sky-50 text-sky-800"
                          }`}>{existing.state || "submitted"}</span>
                          <span className="font-mono">{existing.hours}h</span>
                          {existing.start_time && existing.end_time && (
                            <span className="font-mono text-muted-foreground">{existing.start_time}–{existing.end_time}</span>
                          )}
                          {existing.rating > 0 && (
                            <span className="text-amber-500 font-mono">{"★".repeat(existing.rating)}</span>
                          )}
                          {existing.billable != null && (
                            <span className={`text-[9px] px-1 py-0.5 border ${existing.billable ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-500"}`}>
                              {existing.billable ? "billable" : "non-bill"}
                            </span>
                          )}
                        </div>
                        {existing.note && (
                          <div className="text-[11px] whitespace-pre-wrap break-words">
                            <span className="text-muted-foreground text-[9px] uppercase tracking-widest mr-1">Note</span>
                            {existing.note}
                          </div>
                        )}
                        {existing.review_comment && (
                          <div className="text-[11px] whitespace-pre-wrap break-words bg-amber-50/60 -mx-3 px-3 py-1">
                            <span className="text-amber-700 text-[9px] uppercase tracking-widest mr-1">Reviewer</span>
                            {existing.review_comment}
                          </div>
                        )}
                      </div>
                    )}
                    {task.description && (
                      <div className="text-xs text-muted-foreground line-clamp-4 mb-2"
                            dangerouslySetInnerHTML={{ __html: (task.description || "").slice(0, 400) }} />
                    )}
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px]">
                      {task.priority && <div><span className="text-muted-foreground">Priority:</span> <b className="uppercase">{task.priority}</b></div>}
                      {task.status && <div><span className="text-muted-foreground">Status:</span> <b>{task.status}</b></div>}
                      {task.project_name && <div className="col-span-2 truncate"><span className="text-muted-foreground">Project:</span> <b>{task.project_name}</b></div>}
                      {task.client_name && <div className="col-span-2 truncate"><span className="text-muted-foreground">Client:</span> <b>{task.client_name}</b></div>}
                      {/* Feb 2026 · Content-calendar context, gated by
                          `content_calendar.view`. Lands here when the
                          task was spawned from a calendar deliverable
                          (backend enriches `cc_deliverable_name` +
                          `cc_topic`). */}
                      {hasPerm("content_calendar.view") && task.cc_deliverable_name && (
                        <div className="col-span-2 truncate" data-testid={`ts-task-cc-deliverable-${task.id}`}>
                          <span className="text-muted-foreground">Deliverable:</span> <b>{task.cc_deliverable_name}</b>
                        </div>
                      )}
                      {hasPerm("content_calendar.view") && task.cc_topic && (
                        <div className={`col-span-2 truncate ${task.cc_topic_linked ? "text-emerald-700" : "italic text-muted-foreground"}`}
                             data-testid={`ts-task-cc-topic-${task.id}`}>
                          <span className="text-muted-foreground not-italic">Topic:</span> <b>{task.cc_topic}</b>
                        </div>
                      )}
                      {task.due_date && <div><span className="text-muted-foreground">Due:</span> <b>{_fmtDueDate(task.due_date)}</b></div>}
                      {task.created_at && <div><span className="text-muted-foreground">Created:</span> <b>{String(task.created_at).slice(0, 10)}</b></div>}
                    </div>
                    {(task.tags || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(task.tags || []).slice(0, 6).map((t) => (
                          <span key={t} className="text-[10px] px-1.5 py-0.5 border border-border rounded-none bg-muted">{t}</span>
                        ))}
                      </div>
                    )}
                  </HoverCardContent>
                </HoverCard>
                {/* Feb 2026 · Due date line directly below the task name
                     (user request). Readable `DD Mon YYYY` format; the
                     relative badge next to the title still handles
                     overdue / today / soon urgency colouring. */}
                {task.due_date && (
                  <div className="text-[10px] text-muted-foreground truncate"
                       data-testid={`ts-task-due-${task.id}`}>
                    Due <span className="text-foreground/80 font-medium">{_fmtDueDate(task.due_date)}</span>
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground truncate">
                  {task.project_name || task.client_name || (task.tags || []).join(", ") || "—"}
                </div>
                {/* Feb 2026 R292 · Always show the primary assignee on
                    each row (user request). Prior logic hid it when
                    the viewer was the primary assignee which hurt
                    clarity on tasks with many collaborators. We mark
                    "you" explicitly so the row is still unmistakable. */}
                {task.assignee_name && (
                  <div className="text-[10px] text-muted-foreground truncate"
                       data-testid={`ts-task-assignee-${task.id}`}>
                    Assigned to <span className="text-foreground/80 font-medium">
                      {task.assignee_id === user?.id ? `${task.assignee_name} (you)` : task.assignee_name}
                    </span>
                  </div>
                )}
              </div>
              {/* Iter 253z-10 · Deep-link to the Tasks module with the
                  edit drawer auto-opened for this task. */}
              <Link
                to={`/app/tasks?task=${task.id}`}
                className="p-1 text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent hover:border-primary/40 shrink-0"
                title="Open task in Tasks module"
                data-testid={`ts-edit-task-${task.id}`}
              >
                <Pencil className="w-3 h-3" />
              </Link>
            </div>
          </>
        ) : (
          <div className="text-[10px] text-muted-foreground italic pl-3">↳ additional block</div>
        )}
      </td>
      <td className="px-2 py-2 align-top">
        {/* R138ba · Per-row date picker. Today = default. Future ok
            (auto-flips state to `planned`); past disabled via min. */}
        <input type="date" value={entryDate}
                 min={today}
                 onChange={(e) => {
                   const v = e.target.value;
                   if (v && v >= today) { setEntryDate(v); mark(); }
                 }}
                 readOnly={readOnly} disabled={readOnly}
                 className={`w-full h-7 text-[10px] px-1 border font-mono tabular-nums ${readOnly ? "bg-muted/60 border-border text-muted-foreground cursor-not-allowed" : entryDate === today ? "bg-white border-emerald-300" : "bg-amber-50 border-amber-400"}`}
                 title={entryDate === today ? "Today" : `Planning for ${entryDate} — will save as planned block`}
                 data-testid={`ts-date-${task.id}${isFirstRow ? "" : "-x"}`} />
      </td>
      <td className="px-2 py-2 align-top">
        <input type="time" value={start} onChange={(e) => { setStart(e.target.value); mark(); }} readOnly={readOnly} disabled={readOnly} className={`w-full h-7 text-[10px] px-1 border font-mono tabular-nums ${readOnly ? "bg-muted/60 border-border text-muted-foreground cursor-not-allowed" : "bg-white border-emerald-300"}`} data-testid={`ts-start-${task.id}${isFirstRow ? "" : "-x"}`} />
      </td>
      <td className="px-2 py-2 align-top">
        <input type="time" value={end} onChange={(e) => { setEnd(e.target.value); mark(); }} readOnly={readOnly} disabled={readOnly} className={`w-full h-7 text-[10px] px-1 border font-mono tabular-nums ${readOnly ? "bg-muted/60 border-border text-muted-foreground cursor-not-allowed" : "bg-white border-emerald-300"}`} data-testid={`ts-end-${task.id}${isFirstRow ? "" : "-x"}`} />
      </td>
      <td className="px-2 py-2 align-top text-right">
        <input type="number" step="0.25" min="0" max="24" value={hours}
                 onChange={(e) => { setHours(e.target.value); mark(); }}
                 readOnly={readOnly} disabled={readOnly}
                 className={`w-full h-7 text-[10px] px-1 border font-mono tabular-nums text-right ${readOnly ? "bg-muted/60 border-border text-muted-foreground cursor-not-allowed" : "bg-white border-emerald-300"}`} data-testid={`ts-hours-${task.id}${isFirstRow ? "" : "-x"}`} />
      </td>
      <td className="px-2 py-2 align-top">
        {/* Feb 2026 · Comment is MANDATORY (red-star header). 2-row
            textarea so the writer can log context without feeling
            cramped. canSave / canSubmit gate on note.trim(). */}
        <textarea value={note} onChange={(e) => { setNote(e.target.value); mark(); }}
                 readOnly={readOnly} disabled={readOnly}
                 placeholder="What did you do? (required)"
                 rows={2}
                 className={`w-full text-xs px-2 py-1 border resize-y ${readOnly ? "bg-muted/60 border-border text-muted-foreground cursor-not-allowed" : (note.trim() ? "bg-white border-emerald-300" : "bg-white border-rose-300")}`} data-testid={`ts-note-${task.id}${isFirstRow ? "" : "-x"}`} />
      </td>
      <td className="px-2 py-2 align-top">
        <div className="flex items-center gap-1 justify-end">
          {readOnly && existing ? (
            <>
              {/* R63 · Locked state chip w/ colour + rating stars if reviewed */}
              <span className={`text-[10px] px-2 py-1 border font-semibold uppercase tracking-wide ${
                rowState === "approved"
                  ? "border-emerald-500/40 bg-emerald-50 text-emerald-700"
                  : "border-sky-500/40 bg-sky-50 text-sky-700"
              }`}
                    title={`State: ${rowState}. Ask an approver to reject if you need to edit.`}
                    data-testid={`ts-locked-${existing.id}`}>
                {rowState === "approved" ? "✓ Approved" : "Submitted"}
              </span>
              {existing.rating && (
                <span className="text-[10px] px-1.5 py-1 border border-amber-500/40 bg-amber-50 text-amber-800 font-semibold"
                       title={existing.review_comment || "Reviewer rating"}>
                  {"★".repeat(existing.rating)}{"☆".repeat(5 - existing.rating)}
                </span>
              )}
              {/* R63 · Review action for approvers (any user; server checks
                    `timesheet.review` permission). Opens a tiny popover
                    to enter 1-5 stars + comment. */}
              <TimesheetReviewButton entry={existing} onReviewed={onDone} />
            </>
          ) : (
            <>
              {/* R63 · Two-button save flow. "Save" persists as `planned`
                    so users can plan today/tomorrow's blocks in advance.
                    "Submit" locks the row and sends for approval. */}
              <button
                type="button"
                onClick={() => save("planned")}
                disabled={!canSave || saving || submitting}
                className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 border ${canSave && !saving ? "border-border hover:bg-muted" : "border-border text-muted-foreground cursor-not-allowed"}`}
                data-testid={`ts-save-planned-${task.id}${isFirstRow ? "" : "-x"}`}
                title="Save as PLANNED (still editable, not yet submitted for approval)"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                {saving ? "…" : existing ? "Save" : "Plan"}
              </button>
              <button
                type="button"
                onClick={submitEntry}
                disabled={!canSubmit || saving || submitting}
                className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 border ${canSubmit && !submitting ? "bg-primary text-primary-foreground border-primary hover:opacity-90" : "border-border text-muted-foreground cursor-not-allowed"}`}
                data-testid={`ts-submit-${task.id}${isFirstRow ? "" : "-x"}`}
                title="Submit for approval — LOCKS this entry"
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {submitting ? "…" : "Submit"}
              </button>
            </>
          )}
          {canAddMore && (
            /* R63 · Allow "+" on ALL rows (planned/submitted alike) so
                 users can plan multiple time blocks per task ahead of
                 time. Each block goes through its own state machine. */
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center justify-center w-6 h-6 border border-border hover:bg-muted"
              data-testid={`ts-add-${task.id}`}
              title="Add another time block for this task"
            >
              <Plus className="w-3 h-3" />
            </button>
          )}
          {existing && !readOnly && (
            <button
              type="button"
              onClick={remove}
              disabled={deleting}
              className="inline-flex items-center justify-center w-6 h-6 border border-border hover:bg-rose-50 hover:text-rose-600 hover:border-rose-300 text-muted-foreground"
              data-testid={`ts-del-${existing.id}`}
              title="Delete this planned entry"
            >
              {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            </button>
          )}
        </div>
      </td>
    </tr>
    {/* R64.4 · Reviewer comment sub-row — visible when a reviewer left
         a note on this entry. Uses a full-width <td> spanning all 6
         columns so the comment reads inline directly under the row it
         belongs to. Amber-tinted background matches the review chip. */}
    {existing?.review_comment && (
      <tr data-testid={`ts-review-row-${existing.id}`}
          className="bg-amber-50/70 border-b border-amber-200/70">
        <td colSpan={7} className="px-3 py-1.5 text-[11px]">
          <span className="text-amber-800 font-semibold uppercase tracking-widest text-[9px] mr-2">
            Reviewer note{existing.rating ? ` · ${existing.rating}/5` : ""}
          </span>
          <span className="text-amber-950/90 whitespace-pre-wrap break-words">{existing.review_comment}</span>
        </td>
      </tr>
    )}
    </>
  );
}


// R63 · Small popover button that lets a workspace member with
// `timesheet.review` permission attach a 1-5 star rating + comment to
// a submitted/approved time entry. Failure surfaces as a toast so
// non-approvers see a clean "permission denied" without a crash.
function TimesheetReviewButton({ entry, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(entry?.rating || 0);
  const [comment, setComment] = useState(entry?.review_comment || "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!rating && !comment.trim()) {
      toast.error("Give at least a rating or a comment"); return;
    }
    setSaving(true);
    try {
      await api.post(`/time-entries/${entry.id}/review`, {
        rating: rating || null,
        review_comment: comment.trim() || null,
      });
      toast.success("Review saved");
      setOpen(false);
      onReviewed?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Review failed"));
    } finally { setSaving(false); }
  };
  if (!open) {
    return (
      <button type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center justify-center w-6 h-6 border border-border hover:bg-amber-50 hover:border-amber-400 text-muted-foreground"
              title="Rate & comment (needs timesheet.review perm)"
              data-testid={`ts-review-open-${entry.id}`}>
        ★
      </button>
    );
  }
  return (
    <div className="absolute right-2 mt-8 z-[95] bg-background border border-border shadow-lg p-2 w-72"
         data-testid={`ts-review-panel-${entry.id}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Review this entry</div>
      <div className="flex gap-0.5 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button"
                  onClick={() => setRating(n)}
                  className={`text-lg leading-none ${n <= rating ? "text-amber-500" : "text-muted-foreground/40"}`}
                  data-testid={`ts-review-star-${entry.id}-${n}`}>
            ★
          </button>
        ))}
        {rating > 0 && (
          <button type="button" onClick={() => setRating(0)}
                  className="text-[10px] text-muted-foreground hover:text-foreground ml-2">clear</button>
        )}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                placeholder="Optional review comment…" rows={3}
                className="w-full text-xs px-2 py-1 border border-border bg-background"
                data-testid={`ts-review-comment-${entry.id}`} />
      <div className="flex gap-1 justify-end mt-1">
        <button type="button" onClick={() => setOpen(false)}
                className="text-[10px] px-2 py-1 border border-border hover:bg-muted"
                data-testid={`ts-review-cancel-${entry.id}`}>Cancel</button>
        <button type="button" onClick={submit} disabled={saving}
                className="text-[10px] px-2 py-1 bg-primary text-primary-foreground border border-primary hover:opacity-90"
                data-testid={`ts-review-save-${entry.id}`}>
          {saving ? "…" : "Save review"}
        </button>
      </div>
    </div>
  );
}

/**
 * R93 · Compact search-input header for the timesheet popup task list.
 * Matches task title, project, brand, and client (case-insensitive).
 * Shows a live "n of N" counter so users know when a filter is active.
 */
function TimesheetTaskSearchBar({
  query, onChange, matchCount, totalCount,
  projectOptions = [], projectFilter = [], onProjectFilterChange,
  dueRange = "all", onDueRangeChange,
  sortBy = "due_date", sortDir = "asc", onSortByChange, onSortDirChange,
}) {
  if (!totalCount) return null;
  const dueChips = [
    { k: "past",     label: "Past due" },
    { k: "today",    label: "Today"    },
    { k: "pending",  label: "Pending till date" },
    { k: "upcoming", label: "Upcoming" },
    { k: "all",      label: "All"      },
  ];
  const sortOptions = [
    { k: "due_date",   label: "Due date"    },
    { k: "created_at", label: "Created"     },
    { k: "updated_at", label: "Last update" },
  ];
  return (
    <div className="sticky top-0 z-10 bg-background border-b border-border">
      <div className="px-3 py-2 flex items-center gap-2">
        <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search by task, project, brand…"
          className="flex-1 bg-transparent border-none outline-none text-xs placeholder:text-muted-foreground"
          data-testid="ts-widget-task-search"
        />
        {query ? (
          <>
            <span className="text-[10px] text-muted-foreground font-mono" data-testid="ts-widget-search-count">
              {matchCount} of {totalCount}
            </span>
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-muted-foreground hover:text-foreground"
              title="Clear search"
              data-testid="ts-widget-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <span className="text-[10px] text-muted-foreground font-mono" data-testid="ts-widget-count-total">{matchCount} of {totalCount} open</span>
        )}
      </div>
      {/* Filters row: project multi-select · due-range chips · sort */}
      <div className="px-3 pb-2 flex items-center gap-2 flex-wrap" data-testid="ts-widget-filters">
        <MultiSelect
          options={projectOptions}
          value={projectFilter}
          onChange={onProjectFilterChange}
          placeholder="Project"
          allLabel="All projects"
          maxChips={1}
          width="w-48"
          testid="ts-widget-project-filter"
        />
        <div className="inline-flex items-center border border-border" data-testid="ts-widget-due-range">
          {dueChips.map((c) => (
            <button
              key={c.k}
              type="button"
              onClick={() => onDueRangeChange(c.k)}
              className={`h-7 px-2 text-[11px] border-r border-border last:border-r-0 ${dueRange === c.k ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
              data-testid={`ts-widget-due-${c.k}`}
            >{c.label}</button>
          ))}
        </div>
        <div className="inline-flex items-center border border-border" data-testid="ts-widget-sort">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide px-2">Sort</span>
          <select
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value)}
            className="h-7 text-[11px] bg-background border-l border-border px-1.5 focus:outline-none"
            data-testid="ts-widget-sort-by"
          >
            {sortOptions.map((o) => (
              <option key={o.k} value={o.k}>{o.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => onSortDirChange(sortDir === "asc" ? "desc" : "asc")}
            className="h-7 px-2 border-l border-border hover:bg-muted inline-flex items-center gap-1 text-[11px]"
            title={sortDir === "asc" ? "Ascending — click to flip" : "Descending — click to flip"}
            data-testid="ts-widget-sort-dir"
          >
            {sortDir === "asc" ? <ArrowUpAZ className="w-3 h-3" /> : <ArrowDownAZ className="w-3 h-3" />}
            {sortDir === "asc" ? "Asc" : "Desc"}
          </button>
        </div>
      </div>
    </div>
  );
}


